import moment from 'moment-timezone';

export const TIME_WINDOW = {
  ROLLING_12: 'R12',
  PLAN_YTD_VS_PRIOR_YTD: 'PYTD_VS_PPYTD',
  PLAN_YTD_VS_PRIOR_PLAN_YEAR: 'PYTD_VS_PPY'
};

/**
 * Finds the plan year matching the given start date and the plan year immediately preceding it.
 *
 * @param {Array<{planStartDate: string, planEndDate: string}>} planYearDateRanges - All plan-year ranges.
 * @param {moment.Moment} currentStart - The current plan year's start date.
 * @returns {{ current: {start: moment.Moment, end: moment.Moment} | null, prior: {start: moment.Moment, end: moment.Moment} | null }}
 */
const findAdjacentPlanYears = (planYearDateRanges, currentStart) => {
  const sorted = (Array.isArray(planYearDateRanges) ? planYearDateRanges : [])
    .map((range) => ({
      start: range?.planStartDate ? moment.utc(range.planStartDate, moment.ISO_8601, true) : moment.invalid(),
      end: range?.planEndDate ? moment.utc(range.planEndDate, moment.ISO_8601, true) : moment.invalid()
    }))
    .filter((entry) => entry.start.isValid())
    .sort((a, b) => a.start.valueOf() - b.start.valueOf());

  const currentIndex = sorted.findIndex(
    (entry) => entry.start.format('YYYY-MM') === currentStart.format('YYYY-MM')
  );

  return {
    current: currentIndex >= 0 ? sorted[currentIndex] : null,
    prior: currentIndex > 0 ? sorted[currentIndex - 1] : null
  };
};

/**
 * Trims a prior window to the months that actually have data, or blanks it when none remain.
 *
 * @param {moment.Moment} priorStart
 * @param {moment.Moment} priorEnd
 * @param {string|Date} [minDate] - The oldest date with available data.
 * @param {moment.Moment} max - The latest date with available data (already validated).
 */
const clampPriorPeriodToAvailableData = (priorStart, priorEnd, minDate, max) => {
  const availableStart = minDate ? moment.utc(minDate) : moment.invalid();
  const boundedStart = availableStart.isValid() && availableStart.isAfter(priorStart, 'month')
    ? availableStart
    : priorStart;
  const boundedEnd = max.isBefore(priorEnd, 'month') ? max : priorEnd;

  if (boundedStart.isAfter(boundedEnd, 'month')) {
    return { priorPeriodFrom: '', priorPeriodTo: '' };
  }

  return {
    priorPeriodFrom: boundedStart.format('MM/YYYY'),
    priorPeriodTo: boundedEnd.format('MM/YYYY')
  };
};

/**
 * Calculates current/prior period from/to dates based on the selected time window.
 *
 * @param {string} timeWindow - One of TIME_WINDOW values.
 * @param {string|Date} maxDate - The maximum date (from available-dates endpoint).
 * @param {string|Date} [planStartDate] - The annual plan start date (from masterdata). Required for Plan YTD options.
 * @param {Array<{planStartDate: string, planEndDate: string}>} [planYearDateRanges] - All plan-year ranges from masterdata.
 * @param {string|Date} [minDate] - The minimum date with available data.
 * @returns {{ currentPeriodFrom: string, currentPeriodTo: string, priorPeriodFrom: string, priorPeriodTo: string } | null}
 *   Dates formatted as MM/YYYY, or null if inputs are invalid. The prior period is
 *   trimmed to the months between minDate and maxDate and comes back as two empty
 *   strings when no month in the prior window has data (including when no earlier
 *   plan year is on record). Callers that persist the result must treat blank
 *   priors as "comparison off" rather than as an empty range.
 */
export const calculateTimeWindowDates = (timeWindow, maxDate, planStartDate, planYearDateRanges, minDate) => {
  const max = moment.utc(maxDate);
  if (!max.isValid()) {
    return null;
  }

  let result = null;

  switch (timeWindow) {
    case TIME_WINDOW.ROLLING_12: {
      const priorStart = moment.utc(max).subtract(23, 'months');
      const priorEnd = moment.utc(max).subtract(12, 'months');
      result = {
        currentPeriodTo: max.format('MM/YYYY'),
        currentPeriodFrom: moment.utc(max).subtract(11, 'months').format('MM/YYYY'),
        ...clampPriorPeriodToAvailableData(priorStart, priorEnd, minDate, max)
      };
      break;
    }

    case TIME_WINDOW.PLAN_YTD_VS_PRIOR_YTD:
    case TIME_WINDOW.PLAN_YTD_VS_PRIOR_PLAN_YEAR: {
      const start = moment.utc(planStartDate);
      if (!start.isValid()) {
        return null;
      }

      const { current, prior } = findAdjacentPlanYears(planYearDateRanges, start);

      // Current period runs from the plan-year start through the latest available month,
      // capped at the plan year's own end so completed plan years show the full year.
      const currentEnd = current && current.end.isValid() && current.end.isBefore(max)
        ? current.end.clone()
        : max.clone();
      const elapsedMonths = currentEnd.clone().startOf('month')
        .diff(moment.utc(start).startOf('month'), 'months');
      if (elapsedMonths < 0) {
        return null;
      }

      // No earlier plan year on record (e.g. the oldest plan year is selected): show blank priors
      // rather than backfilling a synthetic prior window against non-existent data.
      if (!prior) {
        result = {
          currentPeriodTo: currentEnd.format('MM/YYYY'),
          currentPeriodFrom: start.format('MM/YYYY'),
          priorPeriodTo: '',
          priorPeriodFrom: ''
        };
        break;
      }

      // Prior period starts at the actual previous plan year's start from masterdata.
      const priorStart = prior.start.clone();
      const priorPlanYearEnd = prior.end.isValid()
        ? prior.end.clone()
        : moment.utc(start).subtract(1, 'month');

      // Prior To differs between the two modes:
      // - PYTD_VS_PPYTD: calculated as Current To minus 12 months (same elapsed YTD window a year earlier).
      // - PYTD_VS_PPY: the raw previous plan year end from masterdata (full prior plan year).
      // They differ only for the latest (in-progress) plan year, where Current To is a partial YTD window.
      const priorPeriodTo = timeWindow === TIME_WINDOW.PLAN_YTD_VS_PRIOR_YTD
        ? currentEnd.clone().subtract(12, 'months')
        : priorPlanYearEnd;
      const boundedPriorPeriod = clampPriorPeriodToAvailableData(priorStart, priorPeriodTo, minDate, max);

      result = {
        currentPeriodTo: currentEnd.format('MM/YYYY'),
        currentPeriodFrom: start.format('MM/YYYY'),
        ...boundedPriorPeriod
      };
      break;
    }

    default:
      break;
  }

  return result;
};
